import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch } from "../api";
import { useI18n } from "../i18n/I18nContext";

import {
  LineChart, Line, BarChart, Bar, ScatterChart, Scatter,
  PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, ReferenceLine,
} from "recharts";

// ── Helpers ───────────────────────────────────────────────────────────────────
const toF = (v) => {
  if (v === null || v === undefined || v === "") return 0;
  if (typeof v === "object") {
    v = v["$n"] ?? v["value"] ?? v["S"] ?? Object.values(v)[0] ?? 0;
  }
  const s = String(v).trim();
  if (s.toUpperCase() === "N/A") return 0;
  const n = parseFloat(s.replace(",", "."));
  return isNaN(n) ? 0 : n;
};

const stackIdOf = (r) => (r?.ID_Stack || r?.IDStack || "").trim();

const q2 = (v) => Math.round(v * 100) / 100;

function parseDate(str) {
  if (!str) return null;
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(str)) {
    const [d, m, y] = str.split("/");
    return new Date(`${y}-${m}-${d}`);
  }
  return new Date(str);
}

function fmtDate(str) {
  if (!str) return "—";
  return new Date(str).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" });
}

const COLORS = {
  green:  "#4ade80", red:    "#f87171", yellow: "#E6FF3E",
  blue:   "#60a5fa", orange: "#fb923c", purple: "#c084fc",
  teal:   "#2dd4bf", gray:   "#64748b", pink: "#f472b6",
};

const PIE_COLORS  = [COLORS.blue, COLORS.orange, COLORS.purple, COLORS.teal];
const RUPT_COLORS = [COLORS.red, COLORS.orange, COLORS.yellow, COLORS.green, COLORS.blue, COLORS.purple];

// ── KPI Card ──────────────────────────────────────────────────────────────────
function KpiCard({ icon, label, value, sub, color }) {
  return (
    <div style={{ ...S.kpiCard, borderTopColor: color }}>
      <div style={S.kpiIcon}>{icon}</div>
      <div style={{ ...S.kpiValue, color }}>{value}</div>
      <div style={S.kpiLabel}>{label}</div>
      {sub && <div style={S.kpiSub}>{sub}</div>}
    </div>
  );
}

// ── Gauge SVG ─────────────────────────────────────────────────────────────────
function Gauge({ label, value, total, color }) {
  const pct  = total > 0 ? Math.round((value / total) * 100) : 0;
  const r    = 52;
  const circ = 2 * Math.PI * r;
  const dash = (pct / 100) * circ;
  return (
    <div style={S.gaugeWrap}>
      <svg width="140" height="140" viewBox="0 0 140 140">
        <circle cx="70" cy="70" r={r} fill="none" stroke="#1e3a50" strokeWidth="12" />
        <circle cx="70" cy="70" r={r} fill="none" stroke={color}
          strokeWidth="12" strokeDasharray={`${dash} ${circ}`}
          strokeLinecap="round" transform="rotate(-90 70 70)"
          style={{ transition: "stroke-dasharray 0.8s ease" }} />
        <text x="70" y="65" textAnchor="middle" fill="#fff" fontSize="22" fontWeight="800">{pct}%</text>
        <text x="70" y="84" textAnchor="middle" fill="#94a3b8" fontSize="11">{value}/{total}</text>
      </svg>
      <div style={S.gaugeLabel}>{label}</div>
    </div>
  );
}

// ── Tooltip custom ────────────────────────────────────────────────────────────
function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={S.tooltip}>
      <div style={{ color: "#E6FF3E", fontWeight: 700, marginBottom: 6, fontSize: 12 }}>{label}</div>
      {payload.map((p, i) => (
        <div key={i} style={{ color: p.color, fontSize: 12, marginBottom: 2 }}>
          {p.name} : <strong>{typeof p.value === "number" ? q2(p.value) : p.value}</strong>
        </div>
      ))}
    </div>
  );
}

function IlssBarTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  if (!d) return null;
  return (
    <div style={S.tooltip}>
      <div style={{ color: "#E6FF3E", fontWeight: 700, marginBottom: 6, fontSize: 12 }}>
        {d.fullStack || d.stack}
      </div>
      <div style={{ color: COLORS.blue, fontSize: 12, marginBottom: 2 }}>
        ILS BDB (MPa) : <strong>{d.bdb > 0 ? q2(d.bdb) : "—"}</strong>
      </div>
      <div style={{ color: COLORS.purple, fontSize: 12, marginBottom: 2 }}>
        ILS 72H (MPa) : <strong>{d.h72 > 0 ? q2(d.h72) : "—"}</strong>
      </div>
      {d.conf && (
        <div style={{ color: "#94a3b8", fontSize: 11, marginTop: 4 }}>Conformité : {d.conf}</div>
      )}
    </div>
  );
}

// ── Composant principal ───────────────────────────────────────────────────────
export default function DashboardFlexionILSS() {
  const { t, ui } = useI18n();

  const navigate = useNavigate();

  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState("");
  const [flexions, setFlexions] = useState([]);
  const [ilssData, setIlssData] = useState([]);
  const [days,     setDays]     = useState(30);
  const [search,   setSearch]   = useState("");

  // ── Chargement ──────────────────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const results = await Promise.allSettled([
          apiFetch("/api/qualiteflexion/list"),
          apiFetch("/api/ilss/list"),
        ]);

        const safe = async (r) => {
          if (r.status === "rejected") return [];
          try {
            const d = await r.value.json();
            return Array.isArray(d) ? d : d.items || [];
          } catch { return []; }
        };

        const [f, i] = await Promise.all(results.map(safe));

        console.log("Flexion item[0]:", JSON.stringify(f[0], null, 2));
        console.log("ILSS item[0]:",   JSON.stringify(i[0], null, 2));

        setFlexions(f);
        setIlssData(i);
      } catch (e) {
        setError("Erreur chargement données Flexion/ILSS.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // ── Filtre ──────────────────────────────────────────────────────────────────
  const filteredFlex = useMemo(() => {
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - days);
    return flexions.filter(r => {
      const d = parseDate(r.Date) || parseDate(r.Timestamp);
      const inPeriod = d && d >= cutoff;
      const inSearch = !search.trim() ||
        stackIdOf(r).toLowerCase().includes(search.toLowerCase());
      return inPeriod && inSearch;
    });
  }, [flexions, days, search]);

  const filteredIlss = useMemo(() => {
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - days);
    return ilssData.filter(r => {
      const d = parseDate(r.Date) || parseDate(r.Timestamp);
      const inPeriod = d && d >= cutoff;
      const inSearch = !search.trim() ||
        stackIdOf(r).toLowerCase().includes(search.toLowerCase());
      return inPeriod && inSearch;
    });
  }, [ilssData, days, search]);

  // ── Calculs ──────────────────────────────────────────────────────────────────
  const stats = useMemo(() => {

    // ── KPIs Flexion
    const bendVals    = filteredFlex.map(r => toF(r.Bending_Modulus_MPa)).filter(v => v > 0);
    const stressVals  = filteredFlex.map(r => toF(r.Stress_Peak_MPa ?? r.Strain_Peak_Perc)).filter(v => v > 0);
    const forceVals   = filteredFlex.map(r => toF(r.Force_Peak_N)).filter(v => v > 0);
    const bendMoy     = bendVals.length   ? q2(bendVals.reduce((a, b)   => a + b, 0) / bendVals.length)   : null;
    const stressMoy   = stressVals.length ? q2(stressVals.reduce((a, b) => a + b, 0) / stressVals.length) : null;
    const forceMoy    = forceVals.length  ? q2(forceVals.reduce((a, b)  => a + b, 0) / forceVals.length)  : null;

    // ── KPIs ILSS
      const ilssNonConf = filteredIlss.filter(r => r.Conformite_ILSS === "NON CONFORME").length;
    const ilssConf    = filteredIlss.filter(r => r.Conformite_ILSS === "CONFORME").length;
    const ilssdel    = filteredIlss.filter(r => r.Conformite_ILSS === "DÉLAMINÉ").length;


    const bdbVals     = filteredIlss.map(r => toF(r.ILS_BDB_MPa)).filter(v => v > 0);
    const h72Vals     = filteredIlss.map(r => toF(r.ILS_72h_MPa)).filter(v => v > 0);
    const bdbMoy      = bdbVals.length ? q2(bdbVals.reduce((a, b) => a + b, 0) / bdbVals.length) : null;
    const h72Moy      = h72Vals.length ? q2(h72Vals.reduce((a, b) => a + b, 0) / h72Vals.length) : null;

    // ── Scatter BendingModulus par stack
    const scatterBending = filteredFlex
      .filter(r => toF(r.Bending_Modulus_MPa) > 0)
      .map((r, i) => ({
        idx:      i + 1,
        stack:    stackIdOf(r) || "—",
        bending:  toF(r.Bending_Modulus_MPa),
        stress:   toF(r.Stress_Peak_MPa ?? r.Strain_Peak_Perc),
        typo:     r.Typologie || "—",
        date:     r.Date || "",
      }));

    // ── Courbe StressPeak par date
    const stressParDate = {};
    filteredFlex.forEach(r => {
      const key = (r.Date || r.Timestamp || "").split("T")[0];
      if (!stressParDate[key]) stressParDate[key] = { date: fmtDate(key), vals: [] };
      if (toF(r.Stress_Peak_MPa ?? r.Strain_Peak_Perc) > 0) {
        stressParDate[key].vals.push(toF(r.Stress_Peak_MPa ?? r.Strain_Peak_Perc));
      }
    });
    const stressCourbe = Object.values(stressParDate)
      .map(d => ({
        date:   d.date,
        stress: d.vals.length ? q2(d.vals.reduce((a, b) => a + b, 0) / d.vals.length) : null,
      }))
      .filter(d => d.stress !== null)
      .sort((a, b) => a.date.localeCompare(b.date));

    // ── Barres ILSS BDB vs 72H par stack (20 derniers)
    // Chaque typologie (BDB / 72H) est un essai distinct : on agrège par stack.
    const stackMap = new Map();
    for (const r of filteredIlss) {
      const sid = stackIdOf(r);
      if (!sid) continue;
      const d = parseDate(r.Date || r.Timestamp) || new Date(0);
      if (!stackMap.has(sid)) {
        stackMap.set(sid, {
          fullStack: sid,
          stack: sid.slice(-8) || sid,
          bdb: 0,
          h72: 0,
          bdbDate: null,
          h72Date: null,
          date: d,
          conf: r.Conformite_ILSS,
        });
      }
      const entry = stackMap.get(sid);
      if (d > entry.date) entry.date = d;
      const bdb = toF(r.ILS_BDB_MPa);
      const h72 = toF(r.ILS_72h_MPa);
      const typo = (r.Typologie || "").toUpperCase().trim();
      if (bdb > 0 && (typo === "BDB" || typo === "MIXTE" || !typo)) {
        if (!entry.bdbDate || d >= entry.bdbDate) {
          entry.bdb = bdb;
          entry.bdbDate = d;
        }
      }
      if (h72 > 0 && (typo === "72H" || typo === "MIXTE" || !typo)) {
        if (!entry.h72Date || d >= entry.h72Date) {
          entry.h72 = h72;
          entry.h72Date = d;
        }
      }
    }
    const ilssBarres = [...stackMap.values()]
      .filter(r => r.bdb > 0 || r.h72 > 0)
      .sort((a, b) => b.date - a.date)
      .slice(0, 20)
      .reverse();

    // ── Camembert Typologie ILSS
    const typoMap = {};
    filteredIlss.forEach(r => {
      const t = (r.Typologie || "INCONNU").trim().toUpperCase();
      typoMap[t] = (typoMap[t] || 0) + 1;
    });
    const typoPie = Object.entries(typoMap).map(([name, value]) => ({ name, value }));

    // ── Camembert Typologie Flexion
    const typoFlexMap = {};
    filteredFlex.forEach(r => {
      const t = (r.Typologie || "INCONNU").trim().toUpperCase();
      typoFlexMap[t] = (typoFlexMap[t] || 0) + 1;
    });
    const typoFlexPie = Object.entries(typoFlexMap).map(([name, value]) => ({ name, value }));

    // ── Fréquence TypRupt
    const ruptMap = {};
    filteredIlss.forEach(r => {
      const t = (r.Typ_Rupt || "INCONNU").trim();
      ruptMap[t] = (ruptMap[t] || 0) + 1;
    });
    const ruptData = Object.entries(ruptMap)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);

    // ── Table derniers ILSS non conformes
    const nonConfIlss = filteredIlss
      .filter(r => r.Conformite_ILSS === "NON CONFORME")
      .sort((a, b) => parseDate(b.Date || b.Timestamp) - parseDate(a.Date || a.Timestamp))
      .slice(0, 10)
      .map(r => ({
        date:  r.Date || (r.Timestamp || "").split("T")[0],
        stack: stackIdOf(r),
        typo:  r.Typologie,
        bdb:   toF(r.ILS_BDB_MPa),
        h72:   toF(r.ILS_72h_MPa),
        rupt:  r.Typ_Rupt,
        op:    r.Operator || r.Operateur,
      }));

    return {
      bendMoy, stressMoy, forceMoy,
      ilssConf,ilssdel, ilssNonConf, bdbMoy, h72Moy,
      scatterBending, stressCourbe,
      ilssBarres, typoPie, typoFlexPie, ruptData, nonConfIlss,
    };
  }, [filteredFlex, filteredIlss]);

  // ── Rendu loader / erreur ──────────────────────────────────────────────────
  if (loading) return (
    <div style={{ ...S.page, display: "flex", justifyContent: "center", alignItems: "center" }}>
      <div style={{ color: "#E6FF3E", fontSize: 16 }}>{ui("Chargement…")}</div>
    </div>
  );
  if (error) return (
    <div style={{ ...S.page, display: "flex", justifyContent: "center", alignItems: "center" }}>
      <div style={{ color: "#f87171" }}>{error}</div>
    </div>
  );

  const totalIlss = stats.ilssConf + stats.ilssNonConf + stats.ilssdel;

  return (
    <div style={S.page}>
      <div style={S.wrap}>

        {/* ── Header ── */}
        <div style={S.header}>
          <div>
            <div style={S.title}>🔬 Flexion & ILSS</div>
            <div style={S.subtitle}>Module de flexion · Contrainte · Résistance interlaminaire</div>
          </div>
          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <input style={S.searchInput} placeholder="🔍 Filtrer par IDStack…"
              value={search} onChange={e => setSearch(e.target.value)} />
            <div style={S.periodBar}>
              {[1, 7, 30, 90, 365].map(d => (
                <button key={d}
                  style={{ ...S.periodBtn, ...(days === d ? S.periodBtnActive : {}) }}
                  onClick={() => setDays(d)}>
                  {d === 365 ? "1an" : `${d}j`}
                </button>
              ))}
            </div>
            <button style={S.backBtn} onClick={() => navigate(-1)}>← Retour</button>
          </div>
        </div>

        {/* ── KPIs ── */}
        <div style={S.kpiRow}>
          <KpiCard icon="📐" label="Bending modulus moy."
            value={stats.bendMoy ? `${stats.bendMoy} MPa` : "—"}
            sub={`${filteredFlex.length} tests flexion`}
            color={COLORS.blue} />
          <KpiCard icon="💥" label="Stress peak moy."
            value={stats.stressMoy ? `${stats.stressMoy} MPa` : "—"}
            sub="contrainte max moy."
            color={COLORS.orange} />
          <KpiCard icon="🏋️" label="Force peak moy."
            value={stats.forceMoy ? `${stats.forceMoy} N` : "—"}
            sub="force max moy."
            color={COLORS.yellow} />
          <KpiCard icon="✅" label="ILSS conformes"
            value={stats.ilssConf}
            sub={`sur ${totalIlss} tests`}
            color={COLORS.green} />
            <KpiCard icon="✅" label="ILSS délaminé"
            value={stats.ilssdel}
            sub={`sur ${totalIlss} tests`}
            color={COLORS.red} />
          <KpiCard icon="❌" label="ILSS non conformes"
            value={stats.ilssNonConf}
            sub={`${totalIlss > 0 ? Math.round(stats.ilssNonConf / totalIlss * 100) : 0}% du total`}
            color={stats.ilssNonConf > 0 ? COLORS.red : COLORS.green} />
          <KpiCard icon="⚡" label="ILSS BDB moy."
            value={stats.bdbMoy ? `${stats.bdbMoy} MPa` : "—"}
            sub={`72H : ${stats.h72Moy ?? "—"} MPa`}
            color={COLORS.purple} />
        </div>

        {/* ── Ligne 2 : Scatter Bending + Courbe StressPeak ── */}
        <div style={S.row}>
          <div style={{ ...S.card, flex: 1 }}>
            <div style={S.cardTitle}>BendingModulus par test</div>
            {stats.scatterBending.length === 0 ? (
              <div style={S.empty}>Aucune donnée flexion sur la période</div>
            ) : (
              <ResponsiveContainer width="100%" height={240}>
                <ScatterChart margin={{ top: 8, right: 20, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e3a50" />
                  <XAxis dataKey="idx" name="Test n°" tick={{ fill: "#64748b", fontSize: 11 }} label={{ value: "Test n°", position: "insideBottom", fill: "#64748b", fontSize: 11, dy: 10 }} />
                  <YAxis dataKey="bending" name="Bending (MPa)" tick={{ fill: "#64748b", fontSize: 11 }} unit=" MPa" />
                  <Tooltip
                    content={({ active, payload }) => {
                      if (!active || !payload?.length) return null;
                      const d = payload[0]?.payload;
                      return (
                        <div style={S.tooltip}>
                          <div style={{ color: "#E6FF3E", fontWeight: 700, fontSize: 12 }}>{d?.stack}</div>
                          <div style={{ color: COLORS.blue,   fontSize: 12 }}>Bending : {d?.bending} MPa</div>
                          <div style={{ color: COLORS.orange, fontSize: 12 }}>Stress  : {d?.stress} MPa</div>
                          <div style={{ color: "#94a3b8",     fontSize: 12 }}>Typo : {d?.typo}</div>
                          <div style={{ color: "#64748b",     fontSize: 11 }}>{d?.date}</div>
                        </div>
                      );
                    }}
                  />
                  <Scatter data={stats.scatterBending}
                    shape={(props) => {
                      const { cx, cy } = props;
                      return <circle cx={cx} cy={cy} r={5} fill={COLORS.blue} fillOpacity={0.8} stroke="#fff" strokeWidth={0.5} />;
                    }}
                  />
                </ScatterChart>
              </ResponsiveContainer>
            )}
          </div>

          <div style={{ ...S.card, flex: 1 }}>
            <div style={S.cardTitle}>Évolution StressPeak moyen (MPa)</div>
            {stats.stressCourbe.length === 0 ? (
              <div style={S.empty}>Aucune donnée sur la période</div>
            ) : (
              <ResponsiveContainer width="100%" height={240}>
                <LineChart data={stats.stressCourbe} margin={{ top: 8, right: 20, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e3a50" />
                  <XAxis dataKey="date" tick={{ fill: "#64748b", fontSize: 11 }} />
                  <YAxis tick={{ fill: "#64748b", fontSize: 11 }} unit=" MPa" />
                  <Tooltip content={<CustomTooltip />} />
                  <Line type="monotone" dataKey="stress" name="StressPeak moy. (MPa)"
                    stroke={COLORS.orange} strokeWidth={2.5} dot={{ r: 3, fill: COLORS.orange }} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* ── Ligne 3 : ILSS BDB vs 72H + Conformité gauge ── */}
        <div style={S.row2}>
          <div style={{ ...S.card, flex: 2 }}>
            <div style={S.cardTitle}>ILSS — BDB vs 72H par stack (20 derniers)</div>
            {stats.ilssBarres.length === 0 ? (
              <div style={S.empty}>Aucune donnée ILSS sur la période</div>
            ) : (
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={stats.ilssBarres} margin={{ top: 8, right: 20, left: -10, bottom: 30 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e3a50" />
                  <XAxis
                    dataKey="fullStack"
                    tick={{ fill: "#64748b", fontSize: 10 }}
                    tickFormatter={(v) => (v || "").slice(-8)}
                    angle={-35}
                    textAnchor="end"
                    interval={0}
                  />
                  <YAxis tick={{ fill: "#64748b", fontSize: 11 }} unit=" MPa" />
                  <Tooltip content={<IlssBarTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 12, color: "#94a3b8" }} />
                  <Bar dataKey="bdb" name="ILS BDB (MPa)" fill={COLORS.blue}   radius={[4, 4, 0, 0]} />
                  <Bar dataKey="h72" name="ILS 72H (MPa)" fill={COLORS.purple} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>

          <div style={{ ...S.card, flex: 1 }}>
            <div style={S.cardTitle}>Conformité ILSS</div>
            <div style={{ display: "flex", justifyContent: "center", paddingTop: 8 }}>
              <Gauge label="CONFORME" value={stats.ilssConf} total={totalIlss} color={COLORS.green} />
            </div>
            <div style={S.confLegend}>
              <div style={{ ...S.dot, background: COLORS.green }} />
              <span style={{ color: COLORS.green, fontSize: 13 }}>Conforme : {stats.ilssConf}</span>
              <div style={{ ...S.dot, background: COLORS.red, marginLeft: 14 }} />
              <span style={{ color: COLORS.red, fontSize: 13 }}>Non conforme : {stats.ilssNonConf}</span>
                <div style={{ ...S.dot, background: COLORS.orange, marginLeft: 14 }} />
              <span style={{ color: COLORS.red, fontSize: 13 }}>Délaminé : {stats.ilssdel}</span>
            </div>
          </div>
        </div>

        {/* ── Ligne 4 : Typologies + TypRupt ── */}
        <div style={S.rowFull}>
          <div style={{ ...S.card, flex: 1 }}>
            <div style={S.cardTitle}>Répartition Typologie — ILSS</div>
            {stats.typoPie.length === 0 ? (
              <div style={S.empty}>Aucune donnée</div>
            ) : (
              <ResponsiveContainer width="100%" height={360}>
                <PieChart>
                  <Pie data={stats.typoPie} dataKey="value" nameKey="name"
                    cx="50%" cy="50%" outerRadius={80}
                    label={({ name, value }) => `${name}: ${value}`}
                    labelLine={{ stroke: "#1e3a50" }}>
                    {stats.typoPie.map((_, i) => (
                      <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ background: "#071e2f", border: "1px solid #1e3a50", borderRadius: 8 }} itemStyle={{ color: "#cbd5e1" }} />
                  <Legend wrapperStyle={{ fontSize: 12, color: "#94a3b8" }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>

          <div style={{ ...S.card, flex: 2 }}>
            <div style={S.cardTitle}>Fréquence Type de Rupture (TypRupt)</div>
            {stats.ruptData.length === 0 ? (
              <div style={S.empty}>Aucune donnée TypRupt</div>
            ) : (
              <ResponsiveContainer width="100%" height={360}>
                <BarChart data={stats.ruptData} layout="vertical"
                  margin={{ top: 8, right: 30, left: 20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e3a50" />
                  <XAxis type="number" tick={{ fill: "#64748b", fontSize: 11 }} allowDecimals={false} />
                  <YAxis type="category" dataKey="name" tick={{ fill: "#94a3b8", fontSize: 12 }} width={60} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="value" name="Occurrences" radius={[0, 4, 4, 0]}>
                    {stats.ruptData.map((_, i) => (
                      <Cell key={i} fill={RUPT_COLORS[i % RUPT_COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* ── Ligne 5 : Table non-conformités ILSS ── */}
        {stats.nonConfIlss.length > 0 && (
          <div style={S.card}>
            <div style={S.cardTitle}>
              ❌ Dernières non-conformités ILSS
              <span style={{ ...S.badge, background: "rgba(248,113,113,0.15)", color: COLORS.red, borderColor: "rgba(248,113,113,0.3)" }}>
                {stats.nonConfIlss.length}
              </span>
            </div>
            <div style={{ overflowX: "auto" }}>
              <table style={S.table}>
                <thead>
                  <tr>
                    {["Date", "ID Stack", "Typologie", "ILS BDB (MPa)", "ILS 72H (MPa)", "TypRupt", "Opérateur"].map(h => (
                      <th key={h} style={S.th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {stats.nonConfIlss.map((r, i) => (
                    <tr key={i} style={i % 2 === 0 ? S.trEven : S.trOdd}>
                      <td style={S.td}>{r.date}</td>
                      <td style={{ ...S.td, color: "#E6FF3E", fontWeight: 700, fontFamily: "monospace" }}>{r.stack}</td>
                      <td style={S.td}>
                        <span style={{ ...S.badge,
                          background: r.typo === "BDB" ? "rgba(96,165,250,0.15)" : r.typo === "72H" ? "rgba(192,132,252,0.15)" : "rgba(251,146,60,0.15)",
                          color:      r.typo === "BDB" ? COLORS.blue : r.typo === "72H" ? COLORS.purple : COLORS.orange,
                          borderColor: r.typo === "BDB" ? "rgba(96,165,250,0.3)" : r.typo === "72H" ? "rgba(192,132,252,0.3)" : "rgba(251,146,60,0.3)",
                        }}>
                          {r.typo || "—"}
                        </span>
                      </td>
                      <td style={{ ...S.td, color: r.bdb > 0 ? COLORS.blue : "#64748b" }}>{r.bdb || "—"}</td>
                      <td style={{ ...S.td, color: r.h72 > 0 ? COLORS.purple : "#64748b" }}>{r.h72 || "—"}</td>
                      <td style={{ ...S.td, color: "#94a3b8" }}>{r.rupt || "—"}</td>
                      <td style={{ ...S.td, color: "#64748b" }}>{r.op || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────
const S = {
  page:          { minHeight: "100vh", background: "linear-gradient(160deg,#041e2e 0%,#063549 60%,#052a3e 100%)", color: "#e2e8f0", fontFamily: "'Inter',sans-serif", padding: "24px 28px" },
  wrap:          { maxWidth: 1600, margin: "0 auto", display: "flex", flexDirection: "column", gap: 20 },
  header:        { display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 },
  title:         { fontSize: 26, fontWeight: 900, background: "linear-gradient(90deg,#fff,#E6FF3E)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" },
  subtitle:      { fontSize: 13, color: "#94a3b8", marginTop: 4 },
  backBtn:       { padding: "9px 18px", borderRadius: 8, border: "1px solid #1e3a50", background: "rgba(255,255,255,0.04)", color: "#cbd5e1", cursor: "pointer", fontWeight: 700, fontSize: 13 },
  searchInput:   { padding: "8px 14px", borderRadius: 8, border: "1px solid #1e3a50", background: "rgba(255,255,255,0.04)", color: "#e2e8f0", fontSize: 13, outline: "none", width: 200 },
  periodBar:     { display: "flex", gap: 4, background: "rgba(255,255,255,0.04)", border: "1px solid #1e3a50", borderRadius: 8, padding: 4 },
  periodBtn:     { padding: "6px 14px", borderRadius: 6, border: "none", background: "transparent", color: "#94a3b8", cursor: "pointer", fontWeight: 700, fontSize: 13 },
  periodBtnActive: { background: "#E6FF3E", color: "#000" },
  kpiRow:        { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px,1fr))", gap: 14 },
  kpiCard:       { background: "rgba(255,255,255,0.03)", border: "1px solid #1e3a50", borderRadius: 14, padding: "18px 20px", display: "flex", flexDirection: "column", alignItems: "center", gap: 6, borderTopWidth: 3, backdropFilter: "blur(8px)" },
  kpiIcon:       { fontSize: 22 },
  kpiValue:      { fontSize: 30, fontWeight: 900 },
  kpiLabel:      { fontSize: 11, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", textAlign: "center" },
  kpiSub:        { fontSize: 11, color: "#64748b" },
  row:           { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 },
  row2:          { display: "grid", gridTemplateColumns: "2fr 1fr", gap: 16 },
  rowFull: { display: "grid", gridTemplateColumns: "1fr 2fr", gap: 16 },
  card:          { background: "rgba(255,255,255,0.03)", border: "1px solid #1e3a50", borderRadius: 14, padding: "20px 22px", backdropFilter: "blur(8px)" },
  cardTitle:     { fontSize: 13, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 16, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" },
  badge:         { display: "inline-flex", alignItems: "center", padding: "3px 10px", borderRadius: 20, fontWeight: 700, fontSize: 11, border: "1px solid", textTransform: "none", letterSpacing: 0 },
  gaugeWrap:     { display: "flex", flexDirection: "column", alignItems: "center", gap: 6 },
  gaugeLabel:    { fontSize: 12, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.07em" },
  confLegend:    { display: "flex", alignItems: "center", gap: 8, justifyContent: "center", marginTop: 12, fontSize: 13, flexWrap: "wrap" },
  dot:           { width: 10, height: 10, borderRadius: "50%", flexShrink: 0 },
  tooltip:       { background: "#071e2f", border: "1px solid #1e3a50", borderRadius: 8, padding: "10px 14px" },
  empty:         { textAlign: "center", padding: "40px 20px", color: "#64748b", fontSize: 13 },
  table:         { width: "100%", borderCollapse: "collapse", fontSize: 13 },
  th:            { padding: "11px 14px", background: "#071e2f", color: "#94a3b8", fontWeight: 700, fontSize: 11, textTransform: "uppercase", letterSpacing: "0.07em", textAlign: "left", borderBottom: "1px solid #1e3a50", whiteSpace: "nowrap" },
  td:            { padding: "11px 14px", borderBottom: "1px solid #0f2a3d", color: "#cbd5e1", whiteSpace: "nowrap" },
  trEven:        { background: "transparent" },
  trOdd:         { background: "rgba(255,255,255,0.015)" },
};